// Text formatting utilities

/**
 * Format special guests for display
 * @param guests Comma-separated string of special guests
 * @returns Formatted special guests string
 */
export function formatSpecialGuests(guests: string | null): string | null {
	if (!guests || guests.trim().length === 0) {
		return null;
	}

	// Split by comma, trim whitespace, and rejoin with proper spacing
	return guests
		.split(",")
		.map((guest) => guest.trim())
		.filter((guest) => guest.length > 0)
		.join(", ");
}

/**
 * Parse special guests into individual names for badge display
 * @param guests Comma-separated string of special guests
 * @returns Array of individual guest names
 */
export function parseSpecialGuestsForBadges(guests: string | null): string[] {
	if (!guests || guests.trim().length === 0) {
		return [];
	}

	// Split by comma, trim whitespace, and filter out empty strings
	return guests
		.split(",")
		.map((guest) => guest.trim())
		.filter((guest) => guest.length > 0);
}

/**
 * Truncate text to specified length with ellipsis
 * @param text Text to truncate
 * @param maxLength Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
	if (text.length <= maxLength) {
		return text;
	}

	return `${text.substring(0, maxLength).trim()}...`;
}

/**
 * Capitalize first letter of each word
 * @param text Text to capitalize
 * @returns Capitalized text
 */
export function capitalizeWords(text: string): string {
	return text
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

/**
 * Transform a kebab-case slug to a proper display name
 * @param slug Kebab-case slug (e.g., "lost-in-dreams")
 * @returns Display name (e.g., "Lost In Dreams")
 */
export function slugToDisplayName(slug: string): string {
	return capitalizeWords(slug.replace(/-/g, " "));
}

/**
 * Create a URL-friendly slug from an artist name
 * Uses URL encoding to preserve all special characters
 * @param name Artist name (e.g., "Martin Garrix", "deadmau5")
 * @returns URL-encoded slug (e.g., "Martin%20Garrix", "deadmau5")
 */
export function createArtistSlug(name: string, slug?: string): string {
	// Always prefer the database slug when available — it's the canonical identifier
	if (slug) return slug;
	// Fallback: generate a kebab-case slug from the name
	// Note: this may not match the DB slug for artists with special characters
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

/**
 * Parse an artist slug back to the original name
 * @param slug URL-encoded slug (e.g., "Martin%20Garrix")
 * @returns Original artist name (e.g., "Martin Garrix")
 */
export function parseArtistSlug(slug: string): string {
	try {
		// Decode the URL-encoded slug
		return decodeURIComponent(slug);
	} catch {
		// If decoding fails, return the slug as-is
		return slug;
	}
}

/**
 * Normalize a slug for comparison by removing accents/diacritics
 * This allows URLs like /artists/tiesto to match slugs like "tiësto"
 * Uses Unicode NFD normalization to decompose characters, then strips combining marks
 * @param slug The slug to normalize (e.g., "tiësto", "adrián-mills")
 * @returns Normalized slug without accents (e.g., "tiesto", "adrian-mills")
 */
export function normalizeSlugForComparison(slug: string): string {
	return slug
		.toLowerCase()
		// NFD normalization decomposes é into e + combining accent
		.normalize("NFD")
		// Remove combining diacritical marks (accents)
		.replace(/[\u0300-\u036f]/g, "");
}
